import { setSharedData } from './../shared/actions';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, mergeMap, switchMap, tap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PayoutsApiService } from 'src/app/services/api-services/payouts-api';
import { WalletApiService } from 'src/app/services/api-services/wallet-api';
import { CustomSnackbarComponent } from 'src/app/shared/components/custom-snackbar/custom-snackbar.component';
import {
  getPayouts,
  getPayoutsSuccess,
  getPayoutsFailure,
  createPayout,
  createPayoutSuccess,
  createPayoutFailure,
  changePayoutStatus,
  changePayoutStatusSuccess,
  changePayoutStatusFailure,
  getWalletBalance,
  getWalletBalanceSuccess,
  getWalletBalanceFailure,
} from './payouts.actions';

@Injectable()
export class PayoutsEffects {
  constructor(
    private actions$: Actions,
    private payoutsApiService: PayoutsApiService,
    private walletApiService: WalletApiService,
    private snackBar: MatSnackBar,
  ) {}

  getPayouts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getPayouts),
      switchMap(({ request }) => {
        const isAdmin = request.isAdmin;
        const apiCall = isAdmin
          ? this.payoutsApiService.getAllAdminPayout(request)
          : this.payoutsApiService.getAllPayout(request);

        return apiCall.pipe(
          mergeMap((res: any) => [getPayoutsSuccess({ data: res }), setSharedData({ data: res })]),
          catchError((error) => [getPayoutsFailure({ error })]),
        );
      }),
    ),
  );

  createPayout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createPayout),
      switchMap(({ request, searchData }) =>
        this.payoutsApiService.createPayout(request).pipe(
          tap((res: any) => {
            if (res.successful) {
              this.snackBar.openFromComponent(CustomSnackbarComponent, {
                data: {
                  title: 'Payout request submitted successfully',
                  type: 'success',
                },
                panelClass: 'success',
                duration: 3000,
                horizontalPosition: 'start',
              });
            }
          }),
          mergeMap((res: any) => {
            return [createPayoutSuccess({ data: res }), getPayouts({ request: searchData })];
          }),
          catchError((error) => {
            return [createPayoutFailure({ error })];
          }),
        ),
      ),
    );
  });

  changePayoutStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(changePayoutStatus),
      switchMap(({ request, file, filterRequest }) =>
        this.payoutsApiService.changePayoutStatus(file, request).pipe(
          tap((res: any) => {
            if (res.successful) {
              const statusMessage =
                request.status === 'Cancelled' ? 'Payout cancelled successfully' : 'Receipt uploaded successfully';
              this.snackBar.openFromComponent(CustomSnackbarComponent, {
                data: {
                  title: statusMessage,
                  type: 'success',
                },
                panelClass: 'success',
                duration: 3000,
                horizontalPosition: 'start',
              });
            }
          }),
          mergeMap((res: any) => {
            return [changePayoutStatusSuccess({ data: res }), getPayouts({ request: filterRequest })];
          }),
          catchError((error) => {
            return [changePayoutStatusFailure({ error })];
          }),
        ),
      ),
    );
  });

  getWalletBalance$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getWalletBalance),
      switchMap(() =>
        this.walletApiService.hostRemainingWalletBalance().pipe(
          mergeMap((res: any) => {
            const balance = res || 0;
            return [getWalletBalanceSuccess({ balance })];
          }),
          catchError((error) => {
            return [getWalletBalanceFailure({ error })];
          }),
        ),
      ),
    );
  });
}
